import React from 'react'

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

function sortNewestFirst(items) {
    return (items || []).slice().sort((a, b) => {
        return new Date(b.lastModified || 0).getTime() - new Date(a.lastModified || 0).getTime()
    })
}

class AssetLibraryModal extends React.Component {
    state = {
        items: [],
        loading: false,
        uploading: false,
        error: ''
    }

    componentDidMount() {
        if (this.props.open) this._load()
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) this._load()
    }

    _load() {
        this.setState({ loading: true, error: '' }, () => {
            this.props.loadAssets(this.props.assetType, (response) => {
                if (!response || response.error) {
                    this.setState({ loading: false, items: [], error: (response && response.message) || 'Unable to load the asset library.' })
                    return
                }
                this.setState({ loading: false, items: sortNewestFirst(response.items) })
            })
        })
    }

    async _upload(event) {
        const input = event.target
        const file = input.files && input.files[0]
        if (!file) return
        this.setState({ uploading: true, error: '' })
        try {
            const dataUrl = await readFileAsDataUrl(file)
            this.props.uploadAsset({
                assetType: this.props.assetType,
                fileName: file.name,
                dataUrl
            }, (response) => {
                if (!response || response.error || !response.asset) {
                    this.setState({ uploading: false, error: (response && response.message) || 'Unable to upload asset.' })
                    return
                }
                const asset = response.asset
                this.setState((prevState) => ({
                    uploading: false,
                    items: sortNewestFirst([asset, ...prevState.items.filter((item) => item.url !== asset.url)])
                }))
                if (this.props.onSelectUploaded) this.props.onSelectUploaded(asset)
            })
        } catch (error) {
            this.setState({ uploading: false, error: 'Unable to read that file.' })
        }
        if (input) input.value = ''
    }

    render() {
        if (!this.props.open) return null

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 px-4 py-6 backdrop-blur-sm">
                <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
                    <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
                        <div>
                            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{this.props.title || 'Asset library'}</h2>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{this.props.description || 'Choose a shared asset or upload a new one into the library.'}</p>
                        </div>
                        <button type="button" className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900" onClick={this.props.onClose}>
                            Close
                        </button>
                    </div>

                    <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-slate-800">
                        <div className="text-sm text-slate-500 dark:text-slate-400">{this.state.loading ? 'Loading library...' : ((this.state.items || []).length + ' assets loaded')}</div>
                        <label className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-[#1cd997] px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:brightness-95">
                            {this.state.uploading ? 'Uploading...' : 'Upload new'}
                            <input type="file" accept="image/*" className="hidden" onChange={(event) => this._upload(event)} />
                        </label>
                    </div>

                    <div className="overflow-y-auto px-6 py-6">
                        {this.state.error ? <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-200">{this.state.error}</div> : null}
                        {this.state.loading ? <div className="text-sm text-slate-500 dark:text-slate-400">Loading assets...</div> : null}
                        {!this.state.loading && (!this.state.items || this.state.items.length === 0) ? <div className="text-sm text-slate-500 dark:text-slate-400">No assets in the library yet.</div> : null}

                        {!this.state.loading && this.state.items && this.state.items.length > 0 ?
                            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                                {this.state.items.map((item) => (
                                    <button
                                        key={item.key || item.url}
                                        type="button"
                                        onClick={() => this.props.onSelect(item)}
                                        className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 text-left transition hover:border-sky-300 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
                                    >
                                        <div className="h-56 w-full bg-slate-100 dark:bg-slate-950">
                                            <div className="h-full w-full bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(' + item.url + ')' }} />
                                        </div>
                                        <div className="space-y-1 px-4 py-4">
                                            <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.name || 'Shared asset'}</div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400">{item.lastModified ? new Date(item.lastModified).toLocaleString() : 'Shared library asset'}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                            : null}
                    </div>
                </div>
            </div>
        )
    }
}

export default AssetLibraryModal
